// app/lib/orderUtils.ts

import { authenticatedFetch } from "@/app/lib/apiUtils";
import {ExtendedOrder, IndividualVideoDetails} from '@/app/api/types';

export async function getExtendedOrder(orderId: string | undefined, userId: string | undefined): Promise<{ data: ExtendedOrder | null, error: string | null }> {
  if (!orderId) {
    return { data: null, error: null };
  }
  try {
    console.warn("${ktorBackendUrl}/ktor/chat/initiate IS BEING CALLED")
    const ktorBackendUrl = process.env.NEXT_PUBLIC_API_URL;
    const response = await authenticatedFetch(`${ktorBackendUrl}/ktor/chat/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orderId, userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { data: null, error: errorData.error || 'An error occurred while fetching the order.' };
    }

    const data: ExtendedOrder = await response.json();
    // If it's a call product, fetch the call token as well
    if (data.productType === 'CALL') {
      console.warn("${ktorBackendUrl}/ktor/chat/initiate/call IS BEING CALLED AS CALL")
      const callTokenResponse = await authenticatedFetch(`${ktorBackendUrl}/ktor/chat/initiate/call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId, userId }),
      });

      if (callTokenResponse.ok) {
        const callTokenData = await callTokenResponse.json();
        data.callToken = callTokenData.callToken;
      } else {
        console.error('Failed to fetch call token');
      }
    }
    return { data, error: null };
  } catch (error) {
    console.error('Failed to fetch ExtendedOrder:', error);
    return { data: null, error: 'An unexpected error occurred.' };
  }
}



/**
 * Fetches a video token for INDIVIDUAL video calls from the Ktor backend.
 * @param {string} userId - The user ID requesting the token.
 * @param {string} orderId - The order ID for the video session.
 * @param {boolean} isGuide - Whether the requester is a guide.
 * @returns {Promise<IndividualVideoDetails | null>} - The video session details or null on failure.
 */
export async function getIndividualVideoToken(
  userId: string,
  orderId: string,
  isGuide: boolean
): Promise<IndividualVideoDetails | null> {
  try {
    const ktorBackendUrl = process.env.NEXT_PUBLIC_API_URL;
    const endpoint = "/ktor/chat/video/individual";

    const url = `${ktorBackendUrl}${endpoint}`;
    const requestBody = {
      userId,
      orderId,
      isGuide
    };

    console.log("Individual Video API URL:", url);
    console.log("REQUEST BODY:", JSON.stringify(requestBody));

    const response = await authenticatedFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Could not read error response");
      console.error("Error Response:", errorText);
      console.error("Failed to fetch individual video session details", response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    return {
      videoToken: data.videoToken,
      channel: data.channel,
      scheduledStart: data.scheduledStart,
      scheduledEnd: data.scheduledEnd,
      guideId: data.guideId,
      customerSecretName: data.customerSecretName,
      guideName: `${data.guideGivenName} ${data.guideFamilyName || ''}`,
      guideGivenName: data.guideGivenName,
      guidePicture: data.guidePicture,
      productType: data.productType || 'VIDEOCALL',
      sessionType: 'INDIVIDUAL',
    };
  } catch (error) {
    console.error("Error fetching individual video session details:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }
    return null;
  }
}




/**
 * Fetches a video token from the Ktor backend for group video calls.
 * @param {string} userId - The user ID requesting the token.
 * @param {string} eventScheduleId - The eventschedule ID for the video session.
 * @param {string} [orderId] - Optional order ID, required for customers but not for guides.
 * @returns {Promise<Object | null>} - The video session details or null on failure.
 */
export async function getGroupVideoToken(
  userId: string,
  eventScheduleId: string,
  orderId?: string
): Promise<{
  videoToken: string;
  channel: string;
  scheduledStart: string;
  scheduledEnd: string;
  guideId: string;
  guideName: string;
  guidePicture: string;
  currentUserId: string;
  currentUserNumericId: number;
  isCurrentUserGuide: boolean;
  participantsMap: Record<string, string>;
  numericUserIds: Record<string, number>;
  maxParticipants: number;
  productType: string;
  sessionType: string;
} | null> {
  try {
    const ktorBackendUrl = process.env.NEXT_PUBLIC_API_URL;
    const endpoint = orderId
      ? "/ktor/chat/video/group/customer"
      : "/ktor/chat/video/group/guide";

    const url = `${ktorBackendUrl}${endpoint}`;
    const requestBody = orderId
      ? { type: 'customer', orderId, userId, eventScheduleId }
      : { type: 'guide', userId, eventScheduleId };

    console.log("API URL:", url);
    console.log("REQUEST BODY:", JSON.stringify(requestBody));

    const response = await authenticatedFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Could not read error response");
      console.error("Error Response:", errorText);
      console.error("Failed to fetch video session details", response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    return {
      videoToken: data.videoToken,
      channel: data.channel,
      scheduledStart: data.scheduledStart,
      scheduledEnd: data.scheduledEnd,
      guideId: data.guideId,
      guideName: `${data.guideGivenName} ${data.guideFamilyName}`,
      guidePicture: data.guidePicture,
      currentUserId: data.currentUserId,
      currentUserNumericId: data.currentUserNumericId,
      isCurrentUserGuide: data.isCurrentUserGuide,
      participantsMap: data.participantsMap,
      numericUserIds: data.numericUserIds,
      maxParticipants: data.maxParticipants,
      productType: data.productType,
      sessionType: data.sessionType,
    };
  } catch (error) {
    console.error("Error fetching video session details:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
    }
    return null;
  }
}